import { useCallback, useEffect, useState } from 'react';
import { supabase, type Profile } from '@/lib/supabase';

export default function MemberManager() {
  const [members, setMembers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'member')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setMembers((data as Profile[]) || []);
    } catch (e) {
      setError('개인회원 목록을 불러오지 못했습니다.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const withdraw = async (member: Profile) => {
    if (
      !window.confirm(
        `'${member.full_name || '이름 없음'}' 회원을 강제 탈퇴시키겠습니까?\n회원 계정과 관련 데이터가 모두 삭제되며 되돌릴 수 없습니다.`,
      )
    ) {
      return;
    }
    setBusy(member.id);
    setError('');
    try {
      const { error: err } = await supabase.rpc('delete_account', { p_user_id: member.id });
      if (err) throw err;
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
    } catch (e) {
      setError('강제 탈퇴 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setBusy(null);
    }
  };

  const activeCount = members.filter((m) => m.status === 'approved').length;

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-heading text-lg font-bold text-foreground-950">개인회원 목록</h2>
          <p className="text-xs text-foreground-500 mt-1">
            가입한 일반회원({members.length}명 · 승인 {activeCount}명)입니다. 필요 시 회원을 강제 탈퇴시킬 수 있어요.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl border border-background-200 divide-y divide-background-100">
        {loading ? (
          <div className="p-8 flex items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : members.length === 0 ? (
          <div className="p-8 text-center text-foreground-500 text-sm">
            아직 가입한 개인회원이 없습니다.
          </div>
        ) : (
          members.map((member) => {
            const isApproved = member.status === 'approved';
            return (
              <div key={member.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 font-semibold flex-shrink-0">
                  {(member.full_name || '?').charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-foreground-950 text-sm">
                      {member.full_name || '이름 없음'}
                    </span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        isApproved
                          ? 'bg-accent-100 text-accent-700'
                          : 'bg-secondary-100 text-secondary-700'
                      }`}
                    >
                      {isApproved ? '승인' : member.status === 'pending' ? '가입 대기' : member.status}
                    </span>
                    {member.phone && (
                      <span className="text-xs text-foreground-500">{member.phone}</span>
                    )}
                  </div>
                  <div className="text-xs text-foreground-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>
                      {member.subscription_plan
                        ? `이용권 ${member.subscription_plan}`
                        : '이용권 미선택'}
                    </span>
                    {member.subscription_expires_at && (
                      <span>
                        · 만료 {new Date(member.subscription_expires_at).toLocaleDateString('ko-KR')}
                      </span>
                    )}
                    <span>
                      · 가입 {new Date(member.created_at).toLocaleDateString('ko-KR')}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => withdraw(member)}
                  disabled={busy === member.id}
                  className="whitespace-nowrap text-red-600 hover:bg-red-50 text-xs font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60 shrink-0"
                >
                  {busy === member.id ? '처리 중...' : '강제 탈퇴'}
                </button>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}